import React, { FC, useCallback, useEffect, useState } from 'react'
import { FavoriteAScene } from '@/molecules/FavoriteAScene'
import { FeatureLoginPrompt } from '@/molecules/FeaturePrompts'
import { WhyDoYouLovePrompt } from '@/molecules/FeaturePrompts/WhyDoYouLovePrompt'
import { useCollectiblesPlayer } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerContext'
import { useUser } from '@/services/UserService'

interface FavoriteAScenePromptsProps {
  onSayWhyYouLoveClick?: () => void
}

type PromptNames = 'logInNeeded' | 'whyDoYouLove'
type Prompts = Record<PromptNames, boolean>

const PROMPT_CONFIG: Record<PromptNames, { delayInMillis: number }> = {
  logInNeeded: {
    delayInMillis: 10000,
  },
  whyDoYouLove: {
    delayInMillis: 5000,
  },
}

const DEFAULT_PROMPT_STATE: Prompts = {
  logInNeeded: false,
  whyDoYouLove: false,
}

export const FavoriteASceneContainer: FC<FavoriteAScenePromptsProps> = ({ onSayWhyYouLoveClick }) => {
  const { userId } = useUser()
  const isLoggedIn = Boolean(userId)

  const [prompts, setPrompts] = useState<Prompts>(DEFAULT_PROMPT_STATE)
  const { getCanPromptWhyTheyLoveThis } = useCollectiblesPlayer()

  useEffect(() => {
    setPrompts({ ...DEFAULT_PROMPT_STATE, logInNeeded: !isLoggedIn })
  }, [isLoggedIn, setPrompts])

  useEffect(() => {
    const activePrompt = Object.entries(prompts).find(([_, enabled]) => enabled)

    const clearHidePrompt = setTimeout(() => {
      setPrompts(DEFAULT_PROMPT_STATE)
    }, PROMPT_CONFIG[activePrompt?.[0] as PromptNames]?.delayInMillis || 5000)

    return () => {
      clearTimeout(clearHidePrompt)
    }
  }, [prompts, setPrompts])

  const handleUserReaction = useCallback(() => {
    const shouldShowWhyDoYouLove = userId && getCanPromptWhyTheyLoveThis()

    setPrompts({ logInNeeded: !userId, whyDoYouLove: Boolean(shouldShowWhyDoYouLove) })
  }, [userId, getCanPromptWhyTheyLoveThis, setPrompts])

  const featureName = 'Favorite A Scene'

  return (
    <div className="flex items-center">
      <FeatureLoginPrompt featureName={featureName} show={prompts.logInNeeded} />
      <WhyDoYouLovePrompt onClick={onSayWhyYouLoveClick} featureName={featureName} show={prompts.whyDoYouLove} />
      <FavoriteAScene onUserReaction={handleUserReaction} />
    </div>
  )
}
